import React from 'react';

// Helper for inline formatting (bold, inline code)
const renderInline = (text: string): React.ReactNode => {
    // Split by bold (`**...**`) and inline code (`...`) syntax, keeping the delimiters
    const parts = text.split(/(\*\*.*?\*\*|`.*?`)/g).filter(Boolean);

    return (
        <>
            {parts.map((part, index) => {
                if (part.startsWith('**') && part.endsWith('**')) {
                    return <strong key={index}>{part.slice(2, -2)}</strong>;
                }
                if (part.startsWith('`') && part.endsWith('`')) {
                    return <code key={index} className="bg-gray-200 text-red-600 font-mono text-sm px-1 py-0.5 rounded">{part.slice(1, -1)}</code>;
                }
                return part; // Regular text
            })}
        </>
    );
};

const MarkdownRenderer: React.FC<{ content: string }> = ({ content }) => {
    // First, safely handle multi-line code blocks by splitting them out
    const blocks = content.split(/(```[\s\S]*?```)/g);

    return (
        <div className="prose prose-sm max-w-none text-gray-800">
            {blocks.map((block, blockIndex) => {
                if (!block.trim()) return null;

                // Handle Code Blocks
                if (block.startsWith('```') && block.endsWith('```')) {
                    const codeContent = block.replace(/^```.*\n/, '').slice(0, -3).trim();
                    return (
                        <pre key={blockIndex} className="bg-gray-800 text-white p-3 my-2 rounded-md overflow-x-auto text-sm font-mono">
                            <code>{codeContent}</code>
                        </pre>
                    );
                }

                // Handle other block types by splitting paragraphs
                return block.split(/\n\s*\n/).map((subBlock, subBlockIndex) => {
                    if (!subBlock.trim()) return null;
                    const lines = subBlock.trim().split('\n');
                    
                    // Handle Tables
                    if (lines.length > 1 && lines[0].includes('|') && lines[1].replace(/[-|: ]/g, '').length === 0) {
                        const headers = lines[0].split('|').map(h => h.trim()).filter(Boolean);
                        const rows = lines.slice(2).map(row => row.split('|').map(c => c.trim()).filter(Boolean));
                        return (
                            <div key={`${blockIndex}-${subBlockIndex}`} className="overflow-x-auto border border-gray-200 rounded-md my-2">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-100">
                                        <tr>{headers.map((h, i) => <th key={i} className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">{renderInline(h)}</th>)}</tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {rows.map((row, i) => (
                                            <tr key={i}>{row.map((cell, j) => <td key={j} className="px-4 py-2 text-sm text-gray-700">{renderInline(cell)}</td>)}</tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        );
                    }

                    // Handle Unordered Lists
                    if (lines.every(line => line.trim().startsWith('* ') || line.trim().startsWith('- '))) {
                        return (
                            <ul key={`${blockIndex}-${subBlockIndex}`} className="list-disc list-inside space-y-1 my-2 pl-4">
                                {lines.map((line, i) => <li key={i}>{renderInline(line.trim().substring(2))}</li>)}
                            </ul>
                        );
                    }
                    
                    // Handle Ordered Lists
                    if (lines.every(line => /^\d+\.\s/.test(line.trim()))) {
                        return (
                            <ol key={`${blockIndex}-${subBlockIndex}`} className="list-decimal list-inside space-y-1 my-2 pl-4">
                                {lines.map((line, i) => <li key={i}>{renderInline(line.trim().replace(/^\d+\.\s/, ''))}</li>)}
                            </ol>
                        );
                    }
                    
                    // Handle Paragraphs
                    return <p key={`${blockIndex}-${subBlockIndex}`} className="my-2">{renderInline(subBlock)}</p>;
                }).filter(Boolean);
            })}
        </div>
    );
};

export default MarkdownRenderer;